import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import React, { ChangeEvent, Component } from 'react';

import { ExerciseSearchFilters, ExerciseSearchItem } from '../../api/exercises.interface';
import { EXERCISE_LEVEL_OPTIONS } from './exercise.constants';
import { ExerciseSearchFormValues } from './exercise.types';
import { buildExerciseSearchSummary, createExerciseSearchFormValues, parseExerciseSearchFilters } from './exercise.utils';
import DeleteExerciseDialog from './DeleteExerciseDialog';
import ExerciseResultsTable from './ExerciseResultsTable';

interface Props {
    currentFilters: ExerciseSearchFilters;
    deletingTag?: number;
    emptyStateTitle?: string;
    hasLoadedSearch: boolean;
    items: ExerciseSearchItem[];
    loadingList: boolean;
    onDeleteExercise: (item: ExerciseSearchItem) => void;
    onEditExercise: (index: number) => void;
    onReset: () => void;
    onSearch: (filters: ExerciseSearchFilters) => void;
    onSelectExercise?: (item: ExerciseSearchItem) => void;
    selectedExerciseTags?: number[];
    selectionMode?: 'none' | 'single' | 'multiple';
    submitLabel?: string;
}

interface State {
    pendingDeleteItem?: ExerciseSearchItem;
    searchValidationError?: string;
    values: ExerciseSearchFormValues;
}

class ExerciseSearch extends Component<Props, State> {
    state: State = {
        values: createExerciseSearchFormValues(this.props.currentFilters),
    };

    componentDidUpdate(prevProps: Props) {
        const currentFiltersChanged = JSON.stringify(prevProps.currentFilters) !== JSON.stringify(this.props.currentFilters);

        if (currentFiltersChanged) {
            this.setState({ values: createExerciseSearchFormValues(this.props.currentFilters) });
        }
    }

    onDeleteDialogClose = () => {
        this.setState({ pendingDeleteItem: undefined });
    };

    onDeleteDialogConfirm = () => {
        const { pendingDeleteItem } = this.state;

        if (!pendingDeleteItem) {
            return;
        }

        this.props.onDeleteExercise(pendingDeleteItem);
        this.setState({ pendingDeleteItem: undefined });
    };

    onResetClick = () => {
        this.setState({ searchValidationError: undefined, values: createExerciseSearchFormValues() });
        this.props.onReset();
    };

    onSearchClick = () => {
        const result = parseExerciseSearchFilters(this.state.values);

        if (result.error || !result.filters) {
            this.setState({ searchValidationError: result.error });
            return;
        }

        this.setState({ searchValidationError: undefined });
        this.props.onSearch(result.filters);
    };

    onSearchFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        this.setState((currentState) => ({
            searchValidationError: undefined,
            values: {
                ...currentState.values,
                [name]: value,
            },
        }));
    };

    onSearchKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.onSearchClick();
        }
    };

    onStartDeleteExercise = (item: ExerciseSearchItem) => {
        this.setState({ pendingDeleteItem: item });
    };

    renderEmptyState() {
        const { emptyStateTitle, hasLoadedSearch } = this.props;
        const title = hasLoadedSearch ? 'No exercises matched those filters.' : emptyStateTitle || 'Run a search to start managing exercises.';
        const description = hasLoadedSearch
            ? 'Try broadening the name, level, or tag filters.'
            : 'Search by name, tag, or level to find records and jump into edit and delete workflows.';

        return (
            <Paper sx={{ borderRadius: 3, p: 3, textAlign: 'center' }} variant="outlined">
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 0.75 }} variant="h6">{title}</Typography>
                <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }} variant="body2">{description}</Typography>
            </Paper>
        );
    }

    renderResults() {
        const { deletingTag, hasLoadedSearch, items, loadingList, onEditExercise, onSelectExercise, selectedExerciseTags, selectionMode } = this.props;

        if (loadingList) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!items.length) {
            return this.renderEmptyState();
        }

        return (
            <Box>
                {hasLoadedSearch && (
                    <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                        {buildExerciseSearchSummary(this.props.currentFilters, items.length)}
                    </Typography>
                )}
                <ExerciseResultsTable
                    deletingTag={deletingTag}
                    items={items}
                    onDeleteExercise={this.onStartDeleteExercise}
                    onEditExercise={onEditExercise}
                    onSelectExercise={onSelectExercise}
                    selectedExerciseTags={selectedExerciseTags}
                    selectionMode={selectionMode || 'none'}
                />
            </Box>
        );
    }

    render() {
        const { deletingTag, submitLabel } = this.props;
        const { pendingDeleteItem, searchValidationError, values } = this.state;

        return (
            <Box>
                <Paper sx={{ borderRadius: 3, mb: 2.5, p: { xs: 1.75, md: 2.25 } }} variant="outlined">
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, mb: 1.5 }} variant="h6">Search Exercises</Typography>
                    <Box onKeyDown={this.onSearchKeyDown}>
                        <Grid container spacing={2}>
                            <Grid item md={6} xs={12}>
                                <TextField fullWidth label="Name" name="name" onChange={this.onSearchFieldChange} placeholder="e.g. Bicep Curl" size="small" value={values.name} />
                            </Grid>
                            <Grid item md={3} xs={12}>
                                <TextField fullWidth label="Tag" name="tag" onChange={this.onSearchFieldChange} placeholder="e.g. 12 or 12, 18, 24" size="small" value={values.tag} />
                            </Grid>
                            <Grid item md={3} xs={12}>
                                <TextField fullWidth label="Level" name="level" onChange={this.onSearchFieldChange} select size="small" value={values.level}>
                                    <MenuItem value="">All</MenuItem>
                                    {EXERCISE_LEVEL_OPTIONS.map((level) => (
                                        <MenuItem key={level} value={level}>{level}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                        {searchValidationError && <Alert severity="warning" sx={{ mt: 1.5 }}>{searchValidationError}</Alert>}
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button onClick={this.onSearchClick} size="small" startIcon={<SearchRoundedIcon />} sx={{ fontSize: '0.82rem', px: 1.5, py: 0.7 }} variant="contained">{submitLabel || 'Search'}</Button>
                            <Button onClick={this.onResetClick} size="small" sx={{ fontSize: '0.82rem', px: 1.5, py: 0.7 }} variant="outlined">Clear Filters</Button>
                        </Box>
                    </Box>
                </Paper>
                {this.renderResults()}
                <DeleteExerciseDialog
                    deletingTag={deletingTag}
                    exercise={pendingDeleteItem}
                    onCancel={this.onDeleteDialogClose}
                    onConfirm={this.onDeleteDialogConfirm}
                    visible={Boolean(pendingDeleteItem)}
                />
            </Box>
        );
    }
}

export default ExerciseSearch;